/**
 * Tax Deducted at Source.
 *
 * The vendor bills a gross amount; the payer withholds TDS and pays the vendor
 * the remainder, so three figures have to be kept apart and never conflated:
 *
 *   totalAmount   what the vendor invoiced        (gross — unchanged meaning)
 *   tdsAmount     what is withheld                (never leaves the bank)
 *   netPayable    totalAmount − tdsAmount         (what the bank actually pays)
 *
 * Every function here is pure — no database, no clock — which is what lets the
 * boundary cases be unit-tested directly, the same way the approval rule engine
 * is.
 */
import { applyRate } from './money.js';
/**
 * The taxable value a deduction applies to.
 *
 * TDS is withheld on the value of the supply, not on the GST charged on it, so
 * the subtotal is the right base. Invoices that were only ever read as a single
 * total fall back to `total − tax`, then to the total itself.
 */
export function tdsBaseFor(source) {
    if (typeof source.subtotal === 'number' && source.subtotal > 0)
        return source.subtotal;
    const total = source.totalAmount ?? 0;
    const tax = source.taxAmount ?? 0;
    if (total > 0 && tax > 0 && tax < total)
        return total - tax;
    return total;
}
/**
 * The amount to withhold. Returns 0 whenever the deduction does not apply, so
 * callers never have to branch before storing the figure.
 */
export function computeTds(input) {
    if (!input.tdsApplicable)
        return 0;
    const rate = input.rateBasisPoints ?? 0;
    if (rate <= 0 || input.baseAmount <= 0)
        return 0;
    return applyRate(input.baseAmount, rate);
}
/**
 * What the bank pays: the gross bill less TDS and less whatever was settled
 * from the vendor's advance balance. Clamped at zero: a deduction can never
 * exceed the invoice, and a mis-keyed rate must not produce a negative payment
 * instruction.
 */
export function netPayableFor(totalAmount, tdsAmount, advanceAmount = 0) {
    return Math.max(0, totalAmount - tdsAmount - advanceAmount);
}
/** Convenience for the accounting workbench: both derived figures at once. */
export function tdsBreakdown(totalAmount, input) {
    const tdsAmount = Math.min(computeTds(input), totalAmount);
    return { tdsAmount, netPayable: netPayableFor(totalAmount, tdsAmount) };
}
/**
 * The profile a new invoice from this vendor is pre-filled with. A vendor
 * can be paid under more than one section (e.g. professional fees and rent),
 * so the default is whichever profile is flagged, not simply the first one.
 */
export function defaultTdsProfile(profiles) {
    return profiles.find((profile) => profile.isDefault);
}
/** Works out each line's deduction from its base and rate. */
export function computeTdsLines(lines) {
    return lines.map((line) => ({
        ...(line.label ? { label: line.label } : {}),
        section: line.section,
        rateBasisPoints: line.rateBasisPoints,
        baseAmount: line.baseAmount,
        amount: line.amount ??
            (line.rateBasisPoints > 0 && line.baseAmount > 0
                ? applyRate(line.baseAmount, line.rateBasisPoints)
                : 0),
    }));
}
/** Everything withheld across the lines. */
export function totalTds(lines) {
    return lines.reduce((sum, line) => sum + line.amount, 0);
}
/** The taxable value the lines are withheld on, together. */
export function totalTdsBase(lines) {
    return lines.reduce((sum, line) => sum + line.baseAmount, 0);
}
/** A PAN: five letters, four digits, a letter (ABCDE1234F). */
export const PAN_PATTERN = /^[A-Z]{5}\d{4}[A-Z]$/;
/**
 * The PAN inside an Indian GSTIN: characters 3–12 are the holder's PAN
 * (29ABCDE1234F1Z5 → ABCDE1234F). Undefined for anything that is not a GSTIN,
 * such as a VAT number abroad.
 */
export function panFromGstin(gstin) {
    const value = gstin?.trim().toUpperCase() ?? '';
    if (!/^\d{2}[A-Z]{5}\d{4}[A-Z]\dZ[A-Z\d]$/.test(value))
        return undefined;
    return value.slice(2, 12);
}
//# sourceMappingURL=tds.js.map